import React, { useEffect } from 'react';

import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import TabButton from '@dashboard/components/TabButton';
import DefaultConfirmationMessageForm from './DefaultConfirmationMessageForm';
import CustomConfirmationMessageForm from './CustomConfirmationMessageForm';
import RedirectAfterPaymentForm from './RedirectAfterPaymentForm';
import CheckoutRedirectForm from './CheckoutRedirectForm';
import { usePreview } from '@dashboard/context/PreviewContext';
import { AfterPaymentActionEnum } from '@dashboard/enums/after-payment';
import CanUseFeature, { useCanUseFeature } from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';
import usePage from '@dashboard/hooks/usePage';

function getTabIndex(checkout) {
  if (!checkout.afterPaymentAction) return 0;

  switch (checkout.afterPaymentAction) {
    case AfterPaymentActionEnum.CONFIRMATION:
      return checkout.customizeCheckoutConfirmation ? 1 : 0;
    case AfterPaymentActionEnum.CHECKOUT:
      return 2;
    case AfterPaymentActionEnum.REDIRECT:
      return 3;
    default:
      return 0;
  }
}

const PageConfirmationForm = () => {
  const { page } = usePage();
  const tabIndex = getTabIndex(page);
  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const canUseMultiStepCheckout = useCanUseFeature(Features.MULTI_STEP_CHECKOUT);

  const canUseRedirect = useCanUseFeature(Features.REDIRECT_TO_URL);

  useEffect(() => {
    if (page.afterPaymentAction === AfterPaymentActionEnum.CONFIRMATION) {
      setPreview({
        type: 'confirmationCheckoutMessage',
      });
    }

    return () =>
      setPreview({
        type: 'checkout',
      });
  }, []);

  return (
    <TabGroup defaultIndex={tabIndex}>
      <TabList className="grid grid-cols-2 mt-1 gap-2">
        <Tab className="outline-none">
          {({ selected }) => <TabButton selected={selected}>Confirmation message</TabButton>}
        </Tab>

        <Tab className="outline-none">
          {({ selected }) => <TabButton selected={selected}>Custom message</TabButton>}
        </Tab>

        <Tab className="outline-none" disabled={!canUseMultiStepCheckout}>
          {({ selected }) => (
            <CanUseFeature feature={Features.MULTI_STEP_CHECKOUT} inset>
              <TabButton selected={selected} disabled={!canUseMultiStepCheckout}>
                Next page
              </TabButton>
            </CanUseFeature>
          )}
        </Tab>

        {canUseRedirect ? (
          <Tab className="outline-none">
            {({ selected }) => <TabButton selected={selected}>Redirect to URL</TabButton>}
          </Tab>
        ) : (
          <div>
            <CanUseFeature feature={Features.REDIRECT_TO_URL} inset fill>
              <TabButton>Redirect to URL</TabButton>
            </CanUseFeature>
          </div>
        )}
      </TabList>

      <TabPanels className="mt-4">
        <TabPanel>
          <DefaultConfirmationMessageForm />
        </TabPanel>
        <TabPanel>
          <CustomConfirmationMessageForm />
        </TabPanel>
        <TabPanel>
          <CheckoutRedirectForm />
        </TabPanel>
        {canUseRedirect && (
          <TabPanel>
            <RedirectAfterPaymentForm />
          </TabPanel>
        )}
      </TabPanels>
    </TabGroup>
  );
};

export default PageConfirmationForm;
